import React, { useState, useEffect } from "react";
import { AiOutlineEye, AiOutlineEyeInvisible, AiOutlineLock } from "react-icons/ai";
import { API_CONFIG } from '../config/env';

// API Configuration
const API_BASE_URL = API_CONFIG.BASE_URL;

// API Function for changing password
async function changePasswordAPI(currentPassword, newPassword) {
  const token = localStorage.getItem('authToken');
  
  if (!token) {
    throw new Error('No authentication token found. Please log in again.');
  }
  
  const response = await fetch(`${API_BASE_URL}/user/change-password/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword
    })
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    
    // Handle specific error cases
    if (response.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (response.status === 400) {
      throw new Error(errorData.error || 'Invalid password or request data.');
    } else {
      throw new Error(errorData.error || 'Failed to change password. Please try again.');
    }
  }
  
  const data = await response.json();
  return data;
}

const styles = {
  formWrapper: {
    background: "#f9f9ff",
    border: "1px solid #d1d5db",
    borderRadius: 12,
    maxWidth: 420,
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },
  label: {
    userSelect: "none",
    fontWeight: 600,
    color: "#4338ca",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "10px 44px 10px 15px",
    borderRadius: 8,
    border: "1px solid #c7c9d9",
    fontSize: 15,
    fontWeight: 500,
  },
  toggleBtn: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#6366f1",
    fontSize: 22,
    padding: 0,
    userSelect: "none",
  },
  btnSubmit: {
    background: "#4338ca",
    color: "#fff",
    padding: "11px 0",
    borderRadius: 12,
    border: "none",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 17,
    userSelect: "none",
    marginTop: 8,
    transition: "background 0.3s",
  },
  messageSuccess: {
    color: "#059669",
    fontWeight: 600,
    userSelect: "text",
  },
  messageError: {
    color: "#dc2626",
    fontWeight: 600,
    userSelect: "text",
  },
  inputWrapper: {
    position: "relative",
    width: "100%",
  },
};

export default function ChangePassword() {
  const [form, setForm] = useState({ oldPassword: "", newPassword: "" });
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({ oldPassword: false, newPassword: false });
  const [errors, setErrors] = useState({ oldPassword: "", newPassword: "" });
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });
  const [oldPasswordValid, setOldPasswordValid] = useState(false);

  // Regexes for validation
  const uppercaseRegex = /[A-Z]/;
  const numberRegex = /[0-9]/;
  const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;

  // Validate new password live and update criteria checks
  useEffect(() => {
    const pass = form.newPassword;
    setPasswordChecks({
      length: pass.length >= 6,
      uppercase: uppercaseRegex.test(pass),
      number: numberRegex.test(pass),
      specialChar: specialCharRegex.test(pass),
    });

    if (touched.newPassword) {
      const allValid =
        pass.length >= 6 &&
        uppercaseRegex.test(pass) &&
        numberRegex.test(pass) &&
        specialCharRegex.test(pass);
      setErrors((prev) => ({
        ...prev,
        newPassword: allValid ? "" : "Password does not meet the criteria.",
      }));
    }
  }, [form.newPassword, touched.newPassword]);

  // Validate old password
  useEffect(() => {
    if (touched.oldPassword) {
      setErrors((prev) => ({
        ...prev,
        oldPassword: form.oldPassword.length > 0 ? "" : "Please enter your current password.",
      }));
    }
  }, [form.oldPassword, touched.oldPassword]);

  // Check if old password is correct (this would be validated on submit)
  const checkOldPassword = async () => {
    if (!form.oldPassword) return false;
    
    try {
      // We'll validate this on submit, but for now we'll assume it's valid if it's not empty
      setOldPasswordValid(form.oldPassword.length > 0);
      return form.oldPassword.length > 0;
    } catch (error) {
      setOldPasswordValid(false);
      return false;
    }
  };

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
    setSuccess(false);
  }

  function handleBlur(e) {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    
    if (name === 'oldPassword') {
      checkOldPassword();
    }
  }

  // Form validation
  const isFormValid = 
    !errors.oldPassword &&
    !errors.newPassword &&
    form.oldPassword.length > 0 &&
    form.newPassword.length > 0 &&
    Object.values(passwordChecks).every(Boolean) &&
    form.oldPassword !== form.newPassword;

  async function handleSubmit(e) {
    e.preventDefault();
    
    // Prevent submit if not valid
    if (!isFormValid) {
      setTouched({ oldPassword: true, newPassword: true });
      setError("Please fix validation errors.");
      return;
    }
    
    // Clear previous messages
    setError("");
    setSuccess(false);
    
    // Additional validation
    if (form.oldPassword === form.newPassword) {
      setError("New password must be different from the old password.");
      return;
    }
    
    setLoading(true);
    
    try {
      await changePasswordAPI(form.oldPassword, form.newPassword);
      setSuccess(true);
      setError("");
      setForm({ oldPassword: "", newPassword: "" });
      setTouched({ oldPassword: false, newPassword: false });
      setPasswordChecks({
        length: false,
        uppercase: false,
        number: false,
        specialChar: false,
      });
    } catch (error) {
      setError(error.message || "Failed to change password. Please try again.");
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section>
      <h2
        style={{
          fontSize: 38,
          marginRight: 10,
          color: "#4338ca",
          verticalAlign: "middle",
          filter: "drop-shadow(0px 1px 4px #b5beff77)",
        }}
      >
       <AiOutlineLock /> Change Password
      </h2>
      <form onSubmit={handleSubmit} style={styles.formWrapper}>
        {error && (
          <div role="alert" style={styles.messageError}>
            {error}
          </div>
        )}
        {success && (
          <div role="alert" style={styles.messageSuccess}>
            Password changed successfully!
          </div>
        )}

        <PasswordInput
          label="Old Password"
          name="oldPassword"
          value={form.oldPassword}
          show={showOld}
          toggleShow={() => setShowOld((v) => !v)}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Enter old password"
          error={touched.oldPassword && errors.oldPassword}
        />
        <PasswordInput
          label="New Password"
          name="newPassword"
          value={form.newPassword}
          show={showNew}
          toggleShow={() => setShowNew((v) => !v)}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Enter new password"
          error={touched.newPassword && errors.newPassword}
        />

        {/* Password requirements box */}
        <div style={{ 
          padding: "12px 16px", 
          border: "1px solid #e5e7eb", 
          borderRadius: "8px", 
          backgroundColor: "#f9fafb", 
          fontSize: 14, 
          userSelect: "none" 
        }}>
          <div style={{ marginBottom: "8px", fontWeight: 600, color: "#374151" }}>
            Password Requirements:
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <PasswordLine
              isValid={passwordChecks.length}
              text="Minimum 6 characters"
            />
            <PasswordLine
              isValid={passwordChecks.uppercase}
              text="At least 1 uppercase letter"
            />
            <PasswordLine
              isValid={passwordChecks.number}
              text="At least 1 number"
            />
            <PasswordLine
              isValid={passwordChecks.specialChar}
              text="At least 1 special character (!@#$%^&*)"
            />
          </div>
        </div>
        <button 
          type="submit" 
          style={{
            ...styles.btnSubmit,
            ...(loading || !isFormValid ? {
              background: "#9ca3af",
              cursor: "not-allowed",
              opacity: 0.7
            } : {})
          }}
          disabled={loading || !isFormValid}
        >
          {loading ? "Updating..." : "Update Password"}
        </button>
      </form>
    </section>
  );
}

function PasswordInput({ label, name, value, show, toggleShow, onChange, onBlur, placeholder, error }) {
  return (
    <div style={styles.inputWrapper}>
      <label htmlFor={name} style={styles.label}>
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        style={{
          ...styles.input,
          borderColor: error ? "#dc2626" : "#c7c9d9"
        }}
        autoComplete="off"
      />
      <button
        type="button"
        aria-label={show ? "Hide password" : "Show password"}
        onClick={toggleShow}
        style={styles.toggleBtn}
      >
        {show ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
      </button>
      {error && (
        <div style={{ 
          color: "#dc2626", 
          fontSize: "12px", 
          marginTop: "4px",
          fontWeight: 500
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

function PasswordLine({ isValid, text }) {
  return (
    <div style={{ 
      display: "flex", 
      alignItems: "center", 
      gap: "8px",
      color: isValid ? "#059669" : "#dc2626"
    }}>
      <span style={{ fontSize: "16px" }}>
        {isValid ? "✓" : "✗"}
      </span>
      <span style={{ fontSize: "13px" }}>{text}</span>
    </div>
  );
}
